/*
 * Copyright © 2021-present Protonbase Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Protonbase Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Protonbase Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { EditOutlined } from '@ant-design/icons'
import { PlatformContractInfo, PlatformContractLegalEntity } from 'account-client'
import { Button, Descriptions, DescriptionsProps } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Replicable } from '@/components/Replicable'
import { StateTag } from '@/components/StateTag'
import { TBuyerType } from '@/pages/contract/interface'
import { isProtonBase } from '@/process.env'
import { RouteSupplyData } from '@/routes/RouteSupplyData'
import { accountClient } from '@/services/cloudClient/accountClient'
import { timeFormatter } from '@/utils/timeFormatter'
import { KycEditModal } from './KycEditModal'

export function ContractOverview() {
  const { t } = useTranslation()
  const contract = RouteSupplyData.useContext('contractId').data
  const { id, signID, state, creator, tags, buyerType, commencingOn } = contract
  const [contractEntity, setContractEntity] = useState<PlatformContractLegalEntity>()
  const [kycInfo, setKycInfo] = useState<PlatformContractInfo>()
  const [kycEditModalVisible, setKycEditModalVisible] = useState(false)

  // Contract Information Items
  const contractInfoItems = useMemo<DescriptionsProps['items']>(() => {
    return [
      {
        key: 'Contract ID',
        label: t('Contract ID'),
        children: <Replicable value={signID}>{signID}</Replicable>,
      },
      {
        key: 'State',
        label: t('State'),
        children: <StateTag state={state} />,
      },
      {
        key: 'Contract Name',
        label: t('Contract Name'),
        children: tags,
      },
      {
        key: 'Created At',
        label: t('Created At'),
        children: timeFormatter.format(commencingOn),
      },
      {
        key: 'Creator',
        label: t('Creator'),
        children: creator,
      },
      {
        key: 'Contact Person',
        label: t('Contact Person'),
        children: kycInfo?.contactPerson || contractEntity?.enterprise?.contactPerson || contractEntity?.citizen?.name,
      },
      {
        key: 'Contact Phone',
        label: t('Contact Phone'),
        children: kycInfo?.contactPhone || contractEntity?.enterprise?.contactPhone || contractEntity?.citizen?.contactPhone,
      },
      {
        key: 'Contact Email',
        label: t('Contact Email'),
        children: kycInfo?.contactEmail || contractEntity?.enterprise?.contactEmail || contractEntity?.citizen?.contactEmail,
      },
    ]
  }, [contract, contractEntity, kycInfo, t])

  // Legal Entity Information Items
  const legalEntityItems = useMemo<DescriptionsProps['items']>(() => {
    if (buyerType === TBuyerType.E) {
      // Enterprise Information
      return [
        {
          key: 'Legal Entity Name',
          label: t('Legal Entity Name'),
          children: kycInfo?.enterpriseName || contractEntity?.enterprise?.name,
        },
        {
          key: 'Registered Address',
          label: t('Registered Address'),
          children: kycInfo?.address || contractEntity?.enterprise?.address,
        },
        {
          key: 'Legal Representative',
          label: t('Legal Representative'),
          children: kycInfo?.enterpriseLegalName || contractEntity?.enterprise?.juridicalPerson,
        },
        {
          key: 'Legal Representative ID',
          label: t('Legal Representative ID'),
          children: kycInfo?.enterpriseLegalSocialId || contractEntity?.enterprise?.juridicalPersonSocialID,
        },
        {
          key: 'Business License',
          label: t('Business License'),
          children: isProtonBase ? (contractEntity?.enterprise?.license || '-') : null,
        },

      ]
    } else {
      // Individual Information
      return [
        {
          key: 'Legal Entity Name',
          label: t('Legal Entity Name'),
          children: kycInfo?.citizenName || contractEntity?.citizen?.name,
        },
        {
          key: 'Tax Identification Number',
          label: t('Tax Identification Number (TIN)'),
          children: kycInfo?.citizenSocialId || contractEntity?.citizen?.socialID,
        },
        {
          key: 'Registered Address',
          label: t('Registered Address'),
          children: kycInfo?.address || contractEntity?.citizen?.address,
        },
        {
          key: 'ID Document',
          label: t('ID Document'),
          children: kycInfo?.citizenSocialId || contractEntity?.citizen?.socialID,
        },
        {
          key: 'Country of Residence',
          label: t('Country of Residence'),
          children: '-', // 暂时使用占位符，等待后端添加字段
        },
        {
          key: 'Bank Account Details',
          label: t('Bank Account Details'),
          children: '-', // 暂时使用占位符，等待后端添加字段
        },
        {
          key: 'UBO Declaration',
          label: t('UBO Declaration'),
          children: '-', // 暂时使用占位符，等待后端添加字段
        },
      ]
    }
  }, [contractEntity, buyerType, kycInfo, t])

  useEffect(() => {
    // 获取合同买方信息
    accountClient.contractsApi.getContractBuyer(id)
      .then(res => setContractEntity(res.data))
      .catch(error => {
        console.error('Failed to get contract buyer:', error)
        // 不显示错误消息，因为这是可选的API调用
      })

    // 获取KYC信息
    accountClient.contractsApi.getContractInfo(id)
      .then(res => setKycInfo(res.data))
      .catch(error => {
        console.error('Failed to get KYC info:', error)
        // 不显示错误消息，因为这是可选的API调用
      })
  }, [id])

  const handleKycEditSuccess = () => {
    // 重新获取KYC信息
    accountClient.contractsApi.getContractInfo(id)
      .then(res => setKycInfo(res.data))
      .catch(error => {
        console.error('Failed to get KYC info:', error)
      })
  }

  return (
    <>
      {/* Contract Information Section */}
      <h2 className="title-third">{t('Contract Information')}</h2>
      <div className="common-card">
        <Descriptions items={contractInfoItems} column={2} bordered />
      </div>

      {/* Legal Entity Information Section */}
      <div style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 className="title-third">{t('Legal Entity Information')}</h2>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setKycEditModalVisible(true)}
          >
            {t('Edit')}
          </Button>
        </div>
        <div className="common-card">
          <Descriptions items={legalEntityItems} column={2} bordered />
        </div>
      </div>

      {/* KYC Edit Modal */}
      <KycEditModal
        visible={kycEditModalVisible}
        contractId={id}
        onCancel={() => setKycEditModalVisible(false)}
        onSuccess={handleKycEditSuccess}
      />
    </>
  )
}
